import { Icon, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { AdmissionMetric, GetAdmissionAggregateResponse } from '@schooly/api';
import { getDashboardMetricsColor } from '@schooly/components/charts';
import { InformationIcon, SimpleCard } from '@schooly/style';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { StatisticsCompareLabel } from './DashboardFilters';
import { calculateGenericTrend, formatNumber } from './utils';

const DASHBOARD_STATISTICS_CARDS_COUNT = 3;

const dashboardStatisticsTooltipMap: Record<AdmissionMetric, string> = {
  applied: 'dashboard-statistics-applied-tooltip',
  offered: 'dashboard-statistics-offered-tooltip',
  accepted: 'dashboard-statistics-accepted-tooltip',
};

type DashboardStatisticsProps = {
  total: GetAdmissionAggregateResponse['total'];
  isLoading?: boolean;
  compareLabel: string;
};

export const DashboardStatistics: FC<DashboardStatisticsProps> = ({
  total,
  isLoading,
  compareLabel,
}) => {
  return (
    <Stack direction="row" gap={2} width="100%">
      {!isLoading ? (
        <Stack direction="row" gap={2} width="100%">
          {total.map((item) => (
            <DashboardStatisticsCard
              key={item.name}
              name={item.name}
              value={item.value}
              comparedValue={item.reference_value}
              compareLabel={compareLabel}
            />
          ))}
        </Stack>
      ) : (
        <Stack direction="row" gap={2} width="100%">
          {Array.from({ length: DASHBOARD_STATISTICS_CARDS_COUNT }).map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={112} sx={{ flex: 1 }} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

type DashboardStatisticsCardProps = {
  name: AdmissionMetric;
  value: number | null;
  comparedValue: number | null;
  compareLabel: string;
};

const DashboardStatisticsCard: FC<PropsWithChildren<DashboardStatisticsCardProps>> = ({
  name,
  value,
  comparedValue,
  children,
  compareLabel,
}) => {
  const { formatMessage } = useIntl();
  const label = formatMessage({ id: `dashboard-metric-${name}` });
  return (
    <SimpleCard
      actions={
        <Stack
          alignSelf="flex-end"
          justifyContent="center"
          height="100%"
          sx={{
            padding: (theme) => theme.spacing(0.25, 0.5, 0.5, 0),
          }}
        >
          <DashboardStatisticsTrendComponent currentValue={value} comparedValue={comparedValue} />
        </Stack>
      }
      avatar={
        <Stack
          sx={{
            padding: (theme) => theme.spacing(0.25, 0, 0.25, 0.25),
          }}
        >
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography variant="h3" color="common.grey2">
              {label}
            </Typography>
            <Tooltip
              title={formatMessage({ id: dashboardStatisticsTooltipMap[name] })}
              componentsProps={{
                tooltip: {
                  sx: () => ({
                    width: 240,
                    padding: 1.25,
                  }),
                },
              }}
            >
              <IconButton inverse>
                <InformationIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Typography variant="h1" color={getDashboardMetricsColor()[name]}>
            {value ?? '-'}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography variant="h2" color="common.grey3">
              {formatMessage({ id: 'dashboard-compare-vs' })} {comparedValue ?? '-'}
            </Typography>
            <StatisticsCompareLabel label={compareLabel} />
          </Stack>
        </Stack>
      }
      actionAreaProps={{ disableRipple: true, disableTouchRipple: true }}
      sx={{
        borderRadius: (theme) => theme.spacing(1),
        border: (theme) => `1px solid ${theme.palette.common.light2}`,
        flex: 1,
        '.MuiButtonBase-root': { cursor: 'default' },
        '.MuiCardActionArea-focusHighlight': { background: 'transparent' },
      }}
    >
      {children}
    </SimpleCard>
  );
};

type DashboardStatisticsTrendComponentProps = {
  currentValue: number | null;
  comparedValue: number | null;
};

const DashboardStatisticsTrendComponent: FC<DashboardStatisticsTrendComponentProps> = ({
  currentValue,
  comparedValue,
}) => {
  const { displayText, tooltipKey, backgroundColor, icon, color } = calculateGenericTrend(
    currentValue,
    comparedValue,
    (current, compared) => ((current - compared) / compared) * 100,
    (change) => `${formatNumber(change)}%`,
  );
  const { formatMessage } = useIntl();

  const tooltipTitle = tooltipKey ? formatMessage({ id: tooltipKey }) : '';

  return (
    <TrendComponent
      label={displayText}
      tooltipTitle={tooltipTitle}
      icon={icon}
      backgroundColor={backgroundColor}
      color={color}
    />
  );
};

type TrendComponentProps = {
  label: string;
  tooltipTitle?: string;
  icon?: ReactNode;
  backgroundColor?: string;
  color?: string;
};

export const TrendComponent: FC<TrendComponentProps> = ({
  label,
  tooltipTitle,
  icon,
  backgroundColor,
  color,
}) => {
  return (
    <Tooltip
      title={tooltipTitle}
      componentsProps={{
        tooltip: {
          sx: () => ({
            width: 200,
            padding: 1.25,
          }),
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        sx={{
          backgroundColor: backgroundColor || 'common.main6',
          borderRadius: 0.5,
          px: 0.75,
          py: 0.25,
          '& .MuiTypography-root': {
            color: color || 'common.grey2',
          },
        }}
      >
        {icon && <Icon sx={{ width: 20, height: 20 }}>{icon}</Icon>}
        <Typography variant="h3">{label}</Typography>
      </Stack>
    </Tooltip>
  );
};
